import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Copy } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import {
  DataTable,
  EmptyState,
  Pagination,
  Stat,
  StatGroup,
  type ColumnDef,
} from '@vnetwork/ui/data';
import { Button, Card, CardBody, Skeleton, Tabs, TabsContent, TabsList, TabsTrigger } from '@vnetwork/ui/primitives';
import { Alert, useToast } from '@vnetwork/ui/feedback';
import {
  useMyReferralStats,
  useMyReferralCustomers,
  type ReferralCustomer,
} from '@/api/account';
import { formatMoney } from '@/lib/format';
import { paginationView } from '@/pages/customers/pagination-summary';
import { TableLink } from '@/components/table-link';
import { ReferralClicksTab } from './components/referral-clicks-tab';
import { ReferralConversionsTab } from './components/referral-conversions-tab';
import { ReferralCommissionsTab } from './components/referral-commissions-tab';
import { ReferralPayoutsTab } from './components/referral-payouts-tab';

const PAGE_SIZE = 10;

const BASE_TABS = [
  { value: 'overview', label: 'Overview' },
  { value: 'clicks', label: 'Clicks' },
  { value: 'conversions', label: 'Referral signups' },
] as const;

const COMMISSION_TABS = [
  { value: 'commissions', label: 'Commissions' },
  { value: 'payouts', label: 'Payouts' },
] as const;

type BaseTabValue = (typeof BASE_TABS)[number]['value'];
type CommissionTabValue = (typeof COMMISSION_TABS)[number]['value'];
type TabValue = BaseTabValue | CommissionTabValue;

const ALL_TAB_VALUES: readonly string[] = [
  ...BASE_TABS.map((t) => t.value),
  ...COMMISSION_TABS.map((t) => t.value),
];

/**
 * Overview tab content — shareable link card, live stats and referred customers table.
 * Extracted to keep ReferralPage readable while tabs are added.
 */
function OverviewTab({ earnsCommission }: { earnsCommission: boolean }) {
  const toast = useToast();
  const [page, setPage] = useState(1);

  const stats = useMyReferralStats();
  const customersQuery = useMyReferralCustomers({ page, page_size: PAGE_SIZE });

  const summary = stats.data;
  const rows = customersQuery.data?.results ?? [];
  const pagination = customersQuery.data?.pagination;
  const total = pagination?.total ?? rows.length;
  const pageCount = pagination?.total_pages ?? 1;
  const view = paginationView(page, PAGE_SIZE, rows.length, total, pageCount);

  const columns = useMemo<ColumnDef<ReferralCustomer, unknown>[]>(
    () => [
      {
        accessorKey: 'customer_name',
        header: 'Customer',
        cell: ({ row }) => (
          <TableLink to={`/customers/${row.original.customer_id}`} truncate>
            {row.original.customer_name || '—'}
          </TableLink>
        ),
      },
      {
        accessorKey: 'customer_email',
        header: 'Email',
        cell: ({ getValue }) => (getValue() as string) || '—',
      },
      {
        accessorKey: 'company_name',
        header: 'Company',
        cell: ({ getValue }) => (getValue() as string) || '—',
      },
    ],
    [],
  );

  const copyLink = () => {
    if (!summary?.referral_link) return;
    void navigator.clipboard.writeText(summary.referral_link);
    toast.show({ tone: 'success', title: 'Referral link copied' });
  };

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)', paddingTop: 'var(--vn-space-4)' }}>
      {/* Shareable referral link + copy */}
      <Card flat>
        <CardBody>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--vn-space-3)',
              minWidth: 0,
            }}
          >
            <div style={{ minWidth: 0 }}>
              <div style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>
                Referral link
              </div>
              <div
                style={{
                  fontWeight: 'var(--vn-font-weight-medium)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {summary?.referral_link}
              </div>
            </div>
            <Button
              variant="secondary"
              size="sm"
              startIcon={<Copy size={14} />}
              onClick={copyLink}
              style={{ marginLeft: 'auto', flexShrink: 0 }}
            >
              Copy
            </Button>
          </div>
        </CardBody>
      </Card>

      <Card flat>
        <CardBody>
          <StatGroup columns={earnsCommission ? 3 : 4}>
            <Stat label="Total clicks" dotColor="var(--vn-info-solid)" value={summary?.total_clicks ?? 0} />
            <Stat label="Converted clicks" dotColor="var(--vn-primary-solid)" value={summary?.total_conversions ?? 0} />
            <Stat label="Referred customers" dotColor="var(--vn-success-solid)" value={summary?.referred_customers ?? 0} />
            <Stat label="Conversion rate" dotColor="var(--vn-warning-solid)" value={`${summary?.conversion_rate ?? 0}%`} />
            {earnsCommission && (
              <>
                <Stat
                  label="Confirmed commission"
                  dotColor="var(--vn-success-solid)"
                  value={formatMoney(summary?.confirmed_commission ?? 0)}
                />
                <Stat label="Pending commission" dotColor="var(--vn-warning-solid)" value={formatMoney(summary?.pending_commission ?? 0)} />
              </>
            )}
          </StatGroup>
        </CardBody>
      </Card>

      {customersQuery.isError ? (
        <Alert
          tone="danger"
          title="Failed to load referred customers"
          actions={
            <Button size="sm" variant="ghost" onClick={() => customersQuery.refetch()}>
              Retry
            </Button>
          }
        />
      ) : (
        <DataTable
          columns={columns}
          data={rows}
          getRowId={(r) => String(r.customer_id)}
          enableSorting={false}
          emptyState={<EmptyState title="No referred customers yet" />}
          footer={
            pageCount > 1 ? (
              <Pagination
                page={page}
                pageCount={view.pageCount}
                onPageChange={setPage}
                summary={view.summary}
              />
            ) : (
              view.summary
            )
          }
        />
      )}
    </div>
  );
}

/**
 * Referral — the signed-in employee's own referral dashboard: shareable link,
 * live stats, referred customers, and detailed activity tabs (clicks, signups,
 * commissions, payouts). Commission and Payouts tabs are hidden when the backend
 * reports `earns_commission === false` for this employee.
 */
export default function ReferralPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const stats = useMyReferralStats();
  const earnsCommission = stats.data?.earns_commission ?? false;

  const urlTab = searchParams.get('tab');
  const activeTab: TabValue =
    urlTab && ALL_TAB_VALUES.includes(urlTab) ? (urlTab as TabValue) : 'overview';

  const setTab = (value: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', value);
    setSearchParams(next, { replace: true });
  };

  return (
    <Container size="full">
      <PageHeader
        title="Referral"
        description="Your referral link, performance and referred customers"
      />

      {stats.isError ? (
        <Alert
          tone="danger"
          title="Failed to load referral stats"
          actions={
            <Button size="sm" variant="ghost" onClick={() => stats.refetch()}>
              Retry
            </Button>
          }
        />
      ) : stats.isLoading ? (
        <Card flat>
          <CardBody style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} style={{ height: 24 }} />
            ))}
          </CardBody>
        </Card>
      ) : (
        <Tabs value={activeTab} onValueChange={setTab}>
          <TabsList>
            {BASE_TABS.map((t) => (
              <TabsTrigger key={t.value} value={t.value}>
                {t.label}
              </TabsTrigger>
            ))}
            {earnsCommission &&
              COMMISSION_TABS.map((t) => (
                <TabsTrigger key={t.value} value={t.value}>
                  {t.label}
                </TabsTrigger>
              ))}
          </TabsList>

          <TabsContent value="overview">
            {activeTab === 'overview' && <OverviewTab earnsCommission={earnsCommission} />}
          </TabsContent>

          <TabsContent value="clicks">
            {activeTab === 'clicks' && <ReferralClicksTab />}
          </TabsContent>

          <TabsContent value="conversions">
            {activeTab === 'conversions' && <ReferralConversionsTab />}
          </TabsContent>

          {earnsCommission && (
            <>
              <TabsContent value="commissions">
                {activeTab === 'commissions' && <ReferralCommissionsTab />}
              </TabsContent>

              <TabsContent value="payouts">
                {activeTab === 'payouts' && <ReferralPayoutsTab />}
              </TabsContent>
            </>
          )}
        </Tabs>
      )}
    </Container>
  );
}
